import { useTranslation } from 'react-i18next';
import { useChargePointActions } from '../../hooks/useChargePointActions';
import type {
  ChargePointData,
  ChargePointGroupData,
} from '../../types/charge-point.types';

interface ActionButtonsProps {
  chargePoint: ChargePointData;
  chargePointGroup?: ChargePointGroupData;
}

/**
 * Action buttons for navigation and sharing
 */
export function ActionButtons({ chargePoint }: ActionButtonsProps) {
  const { t } = useTranslation();
  const { startCharging, openDirections } = useChargePointActions();

  const handleStartCharging = () => startCharging(chargePoint);

  const handleShare = async () => {
    const { coordinate, name, id } = chargePoint;
    const shareUrl = `${window.location.origin}?cpId=${id}&lat=${coordinate.lat}&lng=${coordinate.lng}`;

    // Use Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: name || t('chargePoint.shareTitle'),
          text: t('chargePoint.shareText'),
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled or share failed - no action needed
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert(t('chargePoint.linkCopied'));
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const handleGetDirections = () => openDirections(chargePoint.coordinate);

  return (
    <div className="space-y-2">
      {/* Start Charging */}
      <button
        onClick={handleStartCharging}
        className="w-full flex items-center justify-center gap-2 h-12 px-4 bg-monta-blue text-white font-semibold rounded-2xl hover:bg-monta-blue-700 transition-colors"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
        {t('chargePoint.startCharging')}
      </button>

      {/* Actions - 2 columns */}
      <div className="grid grid-cols-2 gap-2">
        {/* Share Location */}
        <button
          onClick={handleShare}
          className="w-full flex items-center justify-center gap-2 h-11 px-4 bg-surface text-ink font-medium rounded-2xl ring-1 ring-inset ring-ink/10 hover:bg-ink/5 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
            />
          </svg>
          {t('chargePoint.shareLocation')}
        </button>

        {/* Get Directions (google maps) */}
        <button
          onClick={handleGetDirections}
          className="flex items-center justify-center gap-2 h-11 px-3 bg-surface text-ink font-medium rounded-2xl ring-1 ring-inset ring-ink/10 hover:bg-ink/5 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          {t('chargePoint.getDirections')}
        </button>
      </div>
    </div>
  );
}
